import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExternalLink } from "lucide-react";

const STATUSES = ["To-Do", "Editing Pending", "Publishing Pending", "Review Pending", "Published"];
const STATUS_CLS = {
  "To-Do": "bg-slate-100 text-slate-700 border-slate-200",
  "Editing Pending": "bg-amber-100 text-amber-700 border-amber-200",
  "Publishing Pending": "bg-orange-100 text-orange-700 border-orange-200",
  "Review Pending": "bg-blue-100 text-blue-700 border-blue-200",
  "Published": "bg-emerald-100 text-emerald-700 border-emerald-200",
};

export default function MyTasks() {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [mine, setMine] = useState("any");
  const [status, setStatus] = useState("");

  useEffect(() => {
    (async () => {
      const params = new URLSearchParams();
      params.set("mine", mine);
      if (status) params.set("status", status);
      const [t, p] = await Promise.all([
        api.get(`/tasks?${params.toString()}`),
        api.get("/projects"),
      ]);
      setTasks(t.data);
      setProjects(p.data);
    })();
  }, [mine, status]);

  const projectMap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin" data-testid="my-tasks-page">
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="px-8 py-4">
          <h1 className="font-display text-2xl font-bold text-slate-900">My Tasks</h1>
          <p className="text-sm text-slate-500 mt-0.5">Tasks assigned to you as a writer or publisher</p>
        </div>
      </div>

      <div className="p-8 space-y-4">
        <div className="flex items-center gap-2">
          <Select value={mine} onValueChange={setMine}>
            <SelectTrigger className="w-40" data-testid="my-tasks-role-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All my tasks</SelectItem>
              <SelectItem value="writer">As writer</SelectItem>
              <SelectItem value="publisher">As publisher</SelectItem>
            </SelectContent>
          </Select>

          <Select value={status || "any"} onValueChange={(v) => setStatus(v === "any" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="my-tasks-status-filter">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>

          <span className="text-sm text-slate-500 ml-auto">{tasks.length} tasks</span>
        </div>

        <Card className="bg-white border-slate-200 overflow-hidden">
          <table className="spreadsheet-table">
            <thead>
              <tr>
                <th className="w-24">Date</th>
                <th>Keyword</th>
                <th>Project</th>
                <th className="w-40">Status</th>
                <th className="w-24">Docs</th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 && (
                <tr><td colSpan={5} className="text-center text-slate-400 py-8">No tasks</td></tr>
              )}
              {tasks.map((t) => (
                <tr key={t.id} data-testid={`my-task-row-${t.id}`}>
                  <td>{t.date}</td>
                  <td>
                    <Link to={`/projects/${t.project_id}`} className="text-slate-900 hover:text-purple-600">
                      {t.keyword || "(empty)"}
                    </Link>
                  </td>
                  <td>
                    <Link to={`/projects/${t.project_id}`} className="text-purple-600 hover:underline text-xs">
                      {projectMap[t.project_id]?.name || "—"}
                    </Link>
                  </td>
                  <td>
                    <Badge className={`${STATUS_CLS[t.status]} border font-medium`} variant="outline">
                      {t.status}
                    </Badge>
                  </td>
                  <td>
                    {t.docs && (
                      <a href={t.docs} target="_blank" rel="noreferrer" className="text-purple-600 hover:underline text-xs flex items-center gap-1">
                        <ExternalLink className="h-3 w-3" /> Open
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
